import { useState, useEffect } from "react";

interface MachineEditState {
  isEditing: boolean;
  selectedMachineId: string;
  machineNotes: string;
  setIsEditing: (value: boolean) => void;
  setSelectedMachineId: (id: string) => void;
  setMachineNotes: (notes: string) => void;
  resetEditState: () => void;
}

interface UseMachineEditStateProps {
  initialMachineId: string;
  initialNotes: string;
}

/**
 * Hook gére l'état d'édition d'une machine
 * Centralise la logique d'édition (activation/désactivation du mode édition, sélection de machine, notes)
 * 
 * @param initialMachineId - ID initial de la machine sélectionnée
 * @param initialNotes - Notes initiales de la machine
 * @returns État et fonctions pour gérer l'édition d'une machine
 */
export function useMachineEditState({
  initialMachineId,
  initialNotes
}: UseMachineEditStateProps): MachineEditState {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedMachineId, setSelectedMachineId] = useState(initialMachineId);
  const [machineNotes, setMachineNotes] = useState(initialNotes || "");

  // useEffect pour mettre à jour l'état interne lorsque les props changent
  // S'assurer que l'état reflète toujours les dernières valeurs des props
  useEffect(() => {
    if (!isEditing) {
      setSelectedMachineId(initialMachineId);
      setMachineNotes(initialNotes || "");
    }
  }, [initialMachineId, initialNotes, isEditing]);

  /**
   * Réinitialise l'état d'édition aux valeurs initiales
   */
  const resetEditState = () => {
    setSelectedMachineId(initialMachineId);
    setMachineNotes(initialNotes || "");
    setIsEditing(false);
  };

  return {
    isEditing,
    selectedMachineId,
    machineNotes,
    setIsEditing,
    setSelectedMachineId,
    setMachineNotes,
    resetEditState,
  };
} 